import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { CircularProgress, Dialog, DialogContent, IconButton, Popover } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

import { useDeleteLabel, useLabelsQuery, useUpdateLabel } from '../../shared';
import { useUpdateRecordLabels } from '../api/mutations';
import LabelCreatorDialog from './LabelCreatorDialog';

const colorPalette = ['#FBBF6D', '#9EDB84', '#C99CE2', '#85C8F7', '#F6A5B5', '#FFD369', '#B0BEC5'];

const NO_IDS: string[] = [];

function LabelEditDialog({ open, onClose, tKey, deviceId, index, currentLabelIds = NO_IDS }: any) {
    const { t } = useTranslation(undefined, { keyPrefix: tKey });
    const [labelSearch, setLabelSearch] = useState('');
    const [creatorOpen, setCreatorOpen] = useState(false);
    const isMobile = useMediaQuery('(max-width: 768px)');

    const { labels, labelsById, isPending: loading } = useLabelsQuery();
    const recordLabels = useUpdateRecordLabels();
    const updateLabel = useUpdateLabel();
    const deleteLabel = useDeleteLabel();
    // The record's ids come from the list cache, which the mutation patches before its request lands.
    const selectedIds: string[] = currentLabelIds;
    const attaching = recordLabels.isPending;
    const canAttach = Boolean(deviceId) && index !== undefined && !attaching;

    // Three-dot menu state; the label is looked up by id so an edit shows in the menu too.
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [menuLabelId, setMenuLabelId] = useState<string | null>(null);
    const menuLabel = menuLabelId ? labelsById[menuLabelId] : null;
    const [editName, setEditName] = useState('');
    const saving = updateLabel.isPending || deleteLabel.isPending;
    const renameInputRef = useRef<HTMLInputElement>(null);
    const chipInputRef = useRef<HTMLInputElement>(null);

    const filteredRecommendations = useMemo(() => {
        const term = labelSearch.trim().toLowerCase();
        if (!term) return labels;
        return labels.filter((label) => label.name?.toLowerCase().startsWith(term));
    }, [labelSearch, labels]);

    useEffect(() => {
        if (!open) {
            setCreatorOpen(false);
            setMenuAnchor(null);
            setMenuLabelId(null);
        }
    }, [open]);

    // Toggle a label on/off for this record
    const toggleLabel = (label: any) => {
        if (!canAttach) return;
        const change = selectedIds.includes(label.id) ? { remove: [label.id] } : { add: [label.id] };
        recordLabels.mutate({ deviceId, index, ...change });
    };

    // Quick-remove a label via the × chip at the top
    const handleQuickRemove = (labelId: string) => {
        if (!canAttach) return;
        recordLabels.mutate({ deviceId, index, remove: [labelId] });
    };

    // The creator has already added the label to the cache; attach it to this record.
    const handleCreated = (label: any) => {
        setCreatorOpen(false);
        if (canAttach && label?.id) recordLabels.mutate({ deviceId, index, add: [label.id] });
    };

    // ··· menu handlers
    const handleOpenMenu = (e: any, label: any) => {
        e.stopPropagation();
        setMenuAnchor(e.currentTarget);
        setMenuLabelId(label.id);
        setEditName(label.name || '');
        setTimeout(() => renameInputRef.current?.focus(), 50);
    };

    const handleCloseMenu = () => {
        setMenuAnchor(null);
        setMenuLabelId(null);
        setEditName('');
    };

    const handleRename = () => {
        const name = editName.trim();
        if (!name || !menuLabel) return;
        updateLabel.mutate({ id: menuLabel.id, update: { name } }, { onSuccess: handleCloseMenu });
    };

    const handleRecolor = (color: string) => {
        if (!menuLabel) return;
        updateLabel.mutate({ id: menuLabel.id, update: { color } });
    };

    const handleDelete = () => {
        if (!menuLabel) return;
        deleteLabel.mutate(menuLabel.id, { onSuccess: handleCloseMenu });
    };

    // Selected label objects for the top chip row; ids of deleted labels drop out here.
    const selectedLabels = selectedIds.map((id) => labelsById[id]).filter(Boolean);

    const showEditorDialog = !isMobile || !creatorOpen;

    return (
        <>
            {showEditorDialog && (
                <Dialog
                    open={open}
                    onClose={onClose}
                    maxWidth='xs'
                    fullWidth
                    PaperProps={{ sx: { borderRadius: '16px', maxHeight: '520px', width: '320px', maxWidth: '320px' } }}
                >
                    <DialogContent sx={{ p: 0 }}>
                        <div className='flex flex-col bg-white'>
                            {/* Inline chip + search bar (Notion-style) */}
                            {/* Clicking the empty padding of the chip area focuses the input, the same
                                affordance a text field's whitespace gives. Only fires when the click
                                lands on the container itself, so chips stay unaffected. Keyboard users
                                reach the input by tabbing to it directly, so there is nothing to add. */}
                            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
                            <div
                                className='flex flex-wrap items-center gap-1 border-b border-[#DFE1E6] bg-[#F9FAFC] px-3 py-2 cursor-text'
                                style={{ minHeight: 44 }}
                                onClick={(e) => {
                                    if (e.target === e.currentTarget) chipInputRef.current?.focus();
                                }}
                            >
                                {selectedLabels.map((label: any) => (
                                    <span
                                        key={label.id}
                                        className='inline-flex items-center gap-0.5 rounded-md px-2 py-0.5 text-[13px] font-normal text-[#3c3c3c] leading-[1.3] flex-none'
                                        style={{ backgroundColor: label.color || '#cccccc' }}
                                    >
                                        {label.name}
                                        <button
                                            type='button'
                                            aria-label={`Remove ${label.name}`}
                                            disabled={attaching}
                                            onClick={(e: any) => {
                                                e.stopPropagation();
                                                handleQuickRemove(label.id);
                                            }}
                                            className='flex-none ml-0.5 rounded-sm opacity-60 hover:opacity-100'
                                        >
                                            <CloseIcon sx={{ fontSize: 12, display: 'block' }} />
                                        </button>
                                    </span>
                                ))}
                                <input
                                    ref={chipInputRef}
                                    value={labelSearch}
                                    onChange={(e: any) => setLabelSearch(e.target.value)}
                                    placeholder={selectedLabels.length === 0 ? t('searchPlaceholder') : ''}
                                    className='flex-1 min-w-[80px] bg-transparent text-[14px] font-normal text-[#53545C] outline-none caret-[#53545C] placeholder:text-[#b0b0b0] py-0.5'
                                />
                            </div>

                            <div className='relative px-4 pt-3 pb-4' style={{ minHeight: '320px' }}>
                                <div className='text-sm font-normal text-[#3c3c3c] mb-2'>
                                    {t('selectOrCreateOption')}
                                </div>
                                <div className='flex flex-col gap-1 overflow-y-auto' style={{ maxHeight: '260px' }}>
                                    {loading ? (
                                        <div className='flex justify-center py-4'>
                                            <CircularProgress size={20} />
                                        </div>
                                    ) : filteredRecommendations.length ? (
                                        filteredRecommendations.map((label: any) => {
                                            const isSelected = selectedIds.includes(label.id);
                                            return (
                                                <div
                                                    key={label.id || label.name}
                                                    className='group flex items-center gap-1'
                                                >
                                                    {/* Toggle button */}
                                                    <button
                                                        className='flex flex-1 items-center gap-2 text-left rounded-md px-1 py-0.5 hover:bg-[#F4F4F5] min-w-0'
                                                        type='button'
                                                        disabled={attaching}
                                                        onClick={() => toggleLabel(label)}
                                                    >
                                                        {/* Checkmark — always reserves space */}
                                                        <span className='flex h-4 w-4 flex-none items-center justify-center'>
                                                            {isSelected && (
                                                                <CheckIcon sx={{ fontSize: 14, color: '#374151' }} />
                                                            )}
                                                        </span>
                                                        <span
                                                            className='inline-flex items-center rounded-md px-2 py-1 text-[14px] font-normal text-[#3c3c3c] leading-[1.2] max-w-full whitespace-nowrap overflow-hidden text-ellipsis'
                                                            style={{ backgroundColor: label.color || '#cccccc' }}
                                                            title={label.name}
                                                        >
                                                            {label.name || t('labelsEmpty')}
                                                        </span>
                                                    </button>

                                                    {/* ··· three-dot — visible on hover */}
                                                    <IconButton
                                                        size='small'
                                                        aria-label='Label options'
                                                        className='opacity-0 group-hover:opacity-100 transition-opacity flex-none'
                                                        onClick={(e: any) => handleOpenMenu(e, label)}
                                                        sx={{ flexShrink: 0 }}
                                                    >
                                                        <MoreHorizIcon fontSize='small' sx={{ color: '#6B7280' }} />
                                                    </IconButton>
                                                </div>
                                            );
                                        })
                                    ) : null}
                                </div>

                                {/* Create button when searching */}
                                {labelSearch.trim() && (
                                    <div className='absolute bottom-0 left-0 w-full bg-white px-4 pb-3 pt-2'>
                                        <button
                                            type='button'
                                            className='flex w-full items-center justify-start gap-2 rounded-md border border-[#DFE1E6] px-3 py-2 text-sm font-medium text-[#3c3c3c] hover:bg-[#F9FAFC]'
                                            onClick={() => setCreatorOpen(true)}
                                        >
                                            <span>{t('create')}</span>
                                            <span className='inline-flex items-center rounded-md bg-[#cccccc] px-2 py-1 text-[14px] font-normal leading-[1.2] text-[#3c3c3c] whitespace-nowrap'>
                                                {labelSearch}
                                            </span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            )}

            <LabelCreatorDialog
                open={creatorOpen}
                onClose={() => setCreatorOpen(false)}
                initialName={labelSearch}
                anchor={isMobile ? 'center' : 'right'}
                hideBackdrop={!isMobile}
                onCreated={handleCreated}
                tKey={tKey}
            />

            {/* ··· options popover — Plan B: anchored to button, opens to the right, auto-flips left near viewport edge */}
            <Popover
                open={Boolean(menuAnchor)}
                anchorEl={menuAnchor}
                onClose={handleCloseMenu}
                anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
                transformOrigin={{ vertical: 'center', horizontal: 'left' }}
                marginThreshold={8}
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        p: 2,
                        minWidth: 210,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
                    },
                }}
            >
                {/* Live preview of selected label */}
                {menuLabel && (
                    <div className='mb-3'>
                        <span
                            className='inline-flex items-center rounded-md px-2 py-1 text-[13px] font-normal text-[#3c3c3c]'
                            style={{ backgroundColor: menuLabel.color || '#cccccc' }}
                        >
                            {menuLabel.name}
                        </span>
                    </div>
                )}

                {/* Color swatches */}
                <div className='flex flex-wrap gap-1.5 mb-3'>
                    {colorPalette.map((c: any) => (
                        <button
                            key={c}
                            type='button'
                            onClick={() => handleRecolor(c)}
                            disabled={saving}
                            title={c}
                            aria-label={c}
                            aria-pressed={(menuLabel?.color || '') === c}
                            style={{
                                width: 22,
                                height: 22,
                                borderRadius: '50%',
                                backgroundColor: c,
                                border: `2.5px solid ${(menuLabel?.color || '') === c ? '#374151' : 'transparent'}`,
                                outline: (menuLabel?.color || '') === c ? '1.5px solid #374151' : 'none',
                                outlineOffset: 1,
                                cursor: saving ? 'not-allowed' : 'pointer',
                                padding: 0,
                                flexShrink: 0,
                            }}
                        />
                    ))}
                </div>

                {/* Rename input */}
                <input
                    ref={renameInputRef}
                    value={editName}
                    onChange={(e: any) => setEditName(e.target.value)}
                    onKeyDown={(e: any) => e.key === 'Enter' && handleRename()}
                    placeholder='Rename label'
                    style={{
                        width: '100%',
                        padding: '6px 10px',
                        borderRadius: 8,
                        border: '1px solid #DFE1E6',
                        fontSize: 14,
                        outline: 'none',
                        boxSizing: 'border-box',
                        color: '#374151',
                    }}
                />
                <button
                    type='button'
                    onClick={handleRename}
                    disabled={saving || !editName.trim()}
                    style={{
                        width: '100%',
                        marginTop: 6,
                        padding: '6px 0',
                        borderRadius: 8,
                        border: '1px solid #DFE1E6',
                        background: '#fff',
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: saving || !editName.trim() ? 'not-allowed' : 'pointer',
                        color: '#374151',
                        opacity: saving || !editName.trim() ? 0.5 : 1,
                    }}
                >
                    {saving ? t('saving') : t('rename')}
                </button>

                <hr style={{ margin: '8px 0', borderColor: '#F3F4F6' }} />

                <button
                    type='button'
                    onClick={handleDelete}
                    disabled={saving}
                    style={{
                        width: '100%',
                        padding: '6px 0',
                        borderRadius: 8,
                        border: 'none',
                        background: 'none',
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: saving ? 'not-allowed' : 'pointer',
                        color: '#DC2626',
                        opacity: saving ? 0.5 : 1,
                    }}
                >
                    {t('deleteLabel')}
                </button>
            </Popover>
        </>
    );
}

export default LabelEditDialog;
